import { FaqArticleSummary, FaqCategoryItem } from 'Modules'

export interface FaqCategoryListCache {
  results: FaqCategoryItem[]
  total: number
  maxCategories: number
}

export function moveItemInList<T extends { _id: string }>(
  items: T[],
  itemId: string,
  dropIndex: number,
): T[] {
  const dragIndex = items.findIndex((row) => row._id === itemId)
  if (dragIndex === -1 || dragIndex === dropIndex) {
    return items
  }
  const next = [...items]
  const [removed] = next.splice(dragIndex, 1)
  next.splice(dropIndex, 0, removed)
  return next
}

export function applyCategoryReorder(
  categories: FaqCategoryItem[],
  movingCategoryId: string,
  dropIndex: number,
): FaqCategoryItem[] {
  return moveItemInList(categories, movingCategoryId, dropIndex)
}

export function applyArticleReorderInCategory(
  categories: FaqCategoryItem[],
  categoryId: string,
  articleId: string,
  dropIndex: number,
): FaqCategoryItem[] {
  return categories.map((category) => {
    if (category._id !== categoryId) {
      return category
    }
    const articles = category.articles ?? []
    return {
      ...category,
      articles: moveItemInList(articles, articleId, dropIndex),
    }
  })
}

export function applyArticleCrossCategoryMove(
  categories: FaqCategoryItem[],
  articleId: string,
  sourceCategoryId: string,
  targetCategoryId: string,
  dropIndex: number,
): FaqCategoryItem[] {
  let movedArticle: FaqArticleSummary | null = null

  const withoutSource = categories.map((category) => {
    if (category._id !== sourceCategoryId) {
      return category
    }
    const articles = category.articles ?? []
    const index = articles.findIndex((row) => row._id === articleId)
    if (index === -1) {
      return category
    }
    movedArticle = articles[index]
    const nextArticles = articles.filter((row) => row._id !== articleId)
    return {
      ...category,
      articles: nextArticles,
      articleCount: Math.max(0, category.articleCount - 1),
    }
  })

  if (!movedArticle) {
    return categories
  }

  return withoutSource.map((category) => {
    if (category._id !== targetCategoryId) {
      return category
    }
    const articles = [...(category.articles ?? [])]
    articles.splice(dropIndex, 0, { ...movedArticle!, categoryId: targetCategoryId })
    return {
      ...category,
      articles,
      articleCount: category.articleCount + 1,
    }
  })
}

/** Merge server fields (e.g. updatedAt after a cross-category move) into the cached article */
export function applyArticleFields(
  categories: FaqCategoryItem[],
  article: FaqArticleSummary
): FaqCategoryItem[] {
  return categories.map((category) => {
    const articles = category.articles ?? []
    if (!articles.some((row) => row._id === article._id)) {
      return category
    }
    return {
      ...category,
      articles: articles.map((row) => (row._id === article._id ? { ...row, ...article } : row)),
    }
  })
}
